import { camelToSnakeCase } from 'ns_libs/formatter';
import {
    IDealFilters,
    IDealFilterOptions,
    NumberRangeOperator,
    DateRangeOperator,
    IDateRangePanel,
    INumberRangePanel,
    IPickListPanel,
    RangeKey,
    IFilterChip,
    IRangeBound,
} from '../types';
import { EMPTY_DEAL_FILTERS, FILTER_PANELS } from '../FiltersPopover/constants';

const PICK_LIST_PANELS = FILTER_PANELS.filter(
    (panel): panel is IPickListPanel => panel.type === 'pick-list',
);

const RANGE_PANELS = FILTER_PANELS.filter(
    (panel): panel is IDateRangePanel | INumberRangePanel => panel.type !== 'pick-list',
);

const getRangeBounds = (panel: IDateRangePanel | INumberRangePanel): IRangeBound[] =>
    (panel.type === 'date-range'
        ? [
            { key: panel.afterKey, chipLabel: panel.afterChipLabel },
            { key: panel.beforeKey, chipLabel: panel.beforeChipLabel },
        ]
        : [
            { key: panel.minKey, chipLabel: panel.minChipLabel },
            { key: panel.maxKey, chipLabel: panel.maxChipLabel },
        ]);

/**
 * Normalize deal filters by merging over empty defaults to guard against stale/partial localStorage shapes
 */
export const normalizeDealFilters = (filters?: Partial<IDealFilters>): IDealFilters => {
    if (!filters) {
        return { ...EMPTY_DEAL_FILTERS };
    }

    const normalized: IDealFilters = { ...EMPTY_DEAL_FILTERS, ...filters };

    PICK_LIST_PANELS.forEach((panel) => {
        normalized[panel.filterKey] = Array.isArray(filters[panel.filterKey]) ? filters[panel.filterKey] : [];
    });

    return normalized;
};

export const hasActiveFilters = (filters?: IDealFilters): boolean => {
    if (!filters) {
        return false;
    }

    const hasPickListFilters = PICK_LIST_PANELS.some(
        (panel) => (filters[panel.filterKey]?.length ?? 0) > 0,
    );

    const hasRangeFilters = RANGE_PANELS.some(
        (panel) => getRangeBounds(panel).some(({ key }) => !!filters[key]),
    );

    return hasPickListFilters || hasRangeFilters;
};

export const buildDealFiltersQueryString = (filters?: IDealFilters): string => {
    if (!filters || !hasActiveFilters(filters)) {
        return '';
    }

    const params = new URLSearchParams();

    PICK_LIST_PANELS.forEach((panel) => {
        filters[panel.filterKey]?.forEach((value) => {
            params.append(camelToSnakeCase(panel.filterKey), value);
        });
    });

    RANGE_PANELS.forEach((panel) => {
        getRangeBounds(panel).forEach(({ key }) => {
            const value = filters[key];
            if (value) {
                params.append(camelToSnakeCase(key), value);
            }
        });
    });

    const queryString = params.toString();
    return queryString ? `&${queryString}` : '';
};

export const buildActiveFilterChips = (
    filters?: IDealFilters,
    options?: IDealFilterOptions,
): IFilterChip[] => {
    if (!filters || !hasActiveFilters(filters)) {
        return [];
    }

    const chips: IFilterChip[] = [];

    const getLabel = (value: string, optionList?: Array<{ value: string; label: string }>): string => {
        const option = optionList?.find((opt) => opt.value === value);
        return option?.label || value;
    };

    PICK_LIST_PANELS.forEach((panel) => {
        filters[panel.filterKey]?.forEach((value) => {
            chips.push({
                id: `${panel.id}-${value}`,
                label: `${panel.chipLabel}: ${getLabel(value, options?.[panel.optionsKey])}`,
                nextFilters: {
                    ...filters,
                    [panel.filterKey]: filters[panel.filterKey]?.filter((v) => v !== value),
                },
            });
        });
    });

    RANGE_PANELS.forEach((panel) => {
        getRangeBounds(panel).forEach(({ key, chipLabel }) => {
            const value = filters[key];
            if (value) {
                chips.push({
                    id: key,
                    label: panel.type === 'number-range' ? `${chipLabel}: $${value}` : `${chipLabel}: ${value}`,
                    nextFilters: {
                        ...filters,
                        [key]: undefined,
                    },
                });
            }
        });
    });

    return chips;
};

export const getFilterKey = (
    op: DateRangeOperator | NumberRangeOperator,
    panelType: 'date-range' | 'number-range',
    panel: IDateRangePanel | INumberRangePanel,
): RangeKey => {
    if (panelType === 'date-range') {
        const datePanel = panel as IDateRangePanel;
        const dateKeyMap: Record<DateRangeOperator, RangeKey> = {
            'After': datePanel.afterKey,
            'Before': datePanel.beforeKey,
        };
        return dateKeyMap[op as DateRangeOperator];
    } else {
        const numberPanel = panel as INumberRangePanel;
        const numberKeyMap: Record<NumberRangeOperator, RangeKey> = {
            '>': numberPanel.minKey,
            '<': numberPanel.maxKey,
        };
        return numberKeyMap[op as NumberRangeOperator];
    }
};

export const toChipDomId = (
    chipId: string,
    index: number
): string => {
    return `filter-chip-${index}-${chipId.replace(/[^a-zA-Z0-9_-]/g, '-')}`;
};